import type { ReactElement } from 'react';
import { Link, NavLink, Outlet } from 'react-router';

import { ApiStatusChips } from './ApiStatusChips';
import { ThemeToggle } from './ThemeToggle';
import { UserChip } from './UserChip';

interface NavItem {
  to: string;
  label: string;
  icon: ReactElement;
}

const ICON_STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function IconStatus() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path d="M4 13.5h4l2.5-6 3 10 2.5-6.5H20" {...ICON_STROKE} />
    </svg>
  );
}

function IconShield() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path d="M12 3.5 19 6v5.5c0 4.3-2.9 7.4-7 9-4.1-1.6-7-4.7-7-9V6l7-2.5Z" {...ICON_STROKE} />
      <path d="M9 12h6" {...ICON_STROKE} />
    </svg>
  );
}

function IconList() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path d="M5 6h14M5 12h14M5 18h9" {...ICON_STROKE} />
    </svg>
  );
}

function IconMetrics() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path d="M5 20V10M12 20V4M19 20v-7" {...ICON_STROKE} />
    </svg>
  );
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Status', icon: <IconStatus /> },
  { to: '/metrics', label: 'Metrics', icon: <IconMetrics /> },
  { to: '/admins', label: 'Admins', icon: <IconShield /> },
  { to: '/audit', label: 'Audit', icon: <IconList /> },
];

/**
 * The app shell: a sidebar (brand, nav, health chips + the machine-view link) +
 * a topbar (theme toggle, user chip) around the routed page.
 */
export function AppLayout() {
  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="sidebar__brand">
          <span className="sidebar__logo" aria-hidden="true">
            iS
          </span>
          <span className="sidebar__title">Backups</span>
        </div>
        <nav className="sidebar__nav" aria-label="Main navigation">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                isActive ? 'sidebar__link sidebar__link--active' : 'sidebar__link'
              }
            >
              <span className="sidebar__icon">{item.icon}</span>
              <span className="sidebar__label">{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebar__footer">
          <ApiStatusChips />
          <Link className="footer-link" to="/health" title="Машиночитаемый /api/health — для внешних мониторов">
            Health JSON →
          </Link>
        </div>
      </aside>

      <div className="shell__main">
        <header className="topbar">
          <div className="topbar__spacer" />
          <ThemeToggle />
          <UserChip />
        </header>
        <main className="page">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
